import React, {useCallback, useRef, useState} from 'react';
import {
    List,
    ExportButton,
    Filter,
    SimpleList,
    SimpleShowLayout,
    NumberField,
    TextField,
    SelectInput,
    TopToolbar,
    sanitizeListRestProps,
    useListContext
} from 'react-admin';
import Show from '../components/admin/Show';
import DataTable from '../components/admin/DataTable';
import ExportRowsButton from '../components/admin/ExportRowsButton';
import FieldDescription from '../components/FieldDescription';
import PalletLedgerTable from '../components/PalletLedgerTable';
import PalletQtyEdit from '../components/PalletQtyEdit';
import {CLIENT_COLUMNS} from '../global/listColumns';
import {showButtonColumnObject, QtyColumnFilter} from '../utils/tableFunctions';
import {setItemDateValues} from '../utils/dataFunctions';
import {blanksLastSort} from '../utils/sortFunctions';
import {viewStyles} from '../global/styles';
import {useMediaQuery} from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import LayersIcon from '@material-ui/icons/Layers';

export const ClientPalletIcon = LayersIcon;

/**
 * View: List
 */

const ListFilter = (props) => (
    <Filter {...props}>
        <SelectInput source="active" label="Active"
                     choices={[
                         {id: true, name: 'Yes'},
                         {id: false, name: 'No'},
                     ]}
                     alwaysOn
                     allowEmpty={false}
        />
    </Filter>
);

const ListTitle = <span><Avatar style={viewStyles.avatar} className="avatar-title"
                                alt="Pallet Storage"><ClientPalletIcon
    style={viewStyles.avatarSvg}/></Avatar>Pallet Storage</span>;

const showButtonColumn = showButtonColumnObject();

// Every sortable column carries a blanks-last sort rule (parrot-media/portal/main#315).
// Clients with no adjustment yet come back without any pallet_* values, and
// react-table's 'basic' sort scrambles a list with values missing.
//
// Every column also has a fixed width (DataTable fixedLayout): most clients
// have blank adjustment columns, so a content-sized table changed shape with
// every sort and page turn as filled and empty cells traded places.
//
// Overrides for the shared client columns are keyed by accessor, so a column
// added to CLIENT_COLUMNS later keeps its own rules rather than inheriting
// Name's (parrot-media/portal/main#318).
const clientColumnOverrides = {
    name: {...blanksLastSort('text'), width: 260}
};
const clientColumns = CLIENT_COLUMNS.map(column => ({...column, ...(clientColumnOverrides[column.accessor] || {})}));

// Pallet count with its Edit pencil alongside the value
const palletQtyColumn = {
    Header: 'Pallets',
    accessor: 'pallet_qty',
    id: 'pallet_qty',
    filter: filterNonZero,
    Filter: QtyColumnFilter,
    ...blanksLastSort('number'),
    width: 120,
    Cell: ({row}) => (
        <span className="cell-pallet-qty">
            {(row.original.pallet_qty) ? row.original.pallet_qty : 0}
            <PalletQtyEdit record={row.original}/>
        </span>
    )
};

// Most recent real count movement: the latest non-zero ledger delta, green
// for increases and red for decreases (parrot-media/portal/main#296).
// Note-only zero-delta entries are ignored by the backend.
const palletLastDeltaColumn = {
    Header: 'Last Change',
    accessor: 'pallet_last_delta',
    ...blanksLastSort('number'),
    width: 110,
    disableFilters: true,
    Cell: ({row}) => {
        const delta = row.original.pallet_last_delta;
        if (!delta) {
            return <span/>;
        }
        return (
            <span style={{color: (delta > 0) ? '#2e7d32' : '#d32f2f', fontWeight: 600}}>
                {(delta > 0) ? `+${delta}` : `${delta}`}
            </span>
        );
    }
};

// Last adjustment summary. These fields come from the latest pallet_ledger
// entry, added to the organizations list response on the backend. Until that
// ships they are absent and render empty.
const palletModifiedColumn = {
    Header: 'Last Adjusted',
    accessor: 'pallet_modified',
    ...blanksLastSort('date'),
    width: 175,
    disableFilters: true,
    Cell: ({row}) => (
        <span>{(row.original.pallet_modified)
            ? setItemDateValues(row.original.pallet_modified).date_string
            : ''}</span>
    )
};

const palletLastNoteColumn = {
    Header: 'Last Reason',
    accessor: 'pallet_last_note',
    ...blanksLastSort('text'),
    width: 220,
    disableFilters: true,
    Cell: ({row}) => <span>{(row.original.pallet_last_note) ? row.original.pallet_last_note : ''}</span>
};

const palletLastUserColumn = {
    Header: 'Last Adjusted By',
    accessor: 'pallet_last_user',
    ...blanksLastSort('text'),
    width: 150,
    disableFilters: true,
    Cell: ({row}) => <span>{(row.original.pallet_last_user) ? row.original.pallet_last_user : ''}</span>
};

// Standing client notes from the latest ledger entry; right-most so long
// values don't crowd the core columns (the table container scrolls).
const palletNotesColumn = {
    Header: 'Notes',
    accessor: 'pallet_notes',
    ...blanksLastSort('text'),
    width: 320,
    disableFilters: true,
    Cell: ({row}) => <span>{(row.original.pallet_notes) ? row.original.pallet_notes : ''}</span>
};

export const listColumns = [
    showButtonColumn,
    ...clientColumns,
    palletQtyColumn,
    palletLastDeltaColumn,
    palletModifiedColumn,
    palletLastNoteColumn,
    palletLastUserColumn,
    palletNotesColumn
];

const listSort = {field: 'name', order: 'ASC'};

// List Actions (Top Toolbar): Export, on the same row as the filters
// (alignment via the .pallet-list-actions CSS override).
// With getRows the Export writes the table's own rows: every page, filtered
// and sorted as on screen (parrot-media/portal/main#318). Without it (the
// small-screen SimpleList has no table) it is React-Admin's fetch-and-write
// button as before.
export const ListActions = (props) => {
    const {className, filters, getRows, rowCount, ...rest} = props;
    const {resource, currentSort, filterValues, total} = useListContext();
    return (
        <TopToolbar className={`pallet-list-actions ${className || ''}`} {...sanitizeListRestProps(rest)}>
            {getRows ? (
                <ExportRowsButton
                    getRows={getRows}
                    disabled={!rowCount}
                    resource={resource}
                />
            ) : (
                <ExportButton
                    disabled={total === 0}
                    resource={resource}
                    sort={currentSort}
                    filterValues={filterValues}
                />
            )}
        </TopToolbar>
    );
};

// Hide clients with a pallet count of 0 when the header checkbox is checked.
// Fractional counts (e.g. 0.5) must stay visible, so filter on > 0 rather
// than the >= 1 threshold used on the Products list.
function filterNonZero(rows, id, filterValue) {
    if (!filterValue) {
        return rows;
    }
    return rows.filter(row => row.values[id] > 0);
}

filterNonZero.autoRemove = val => typeof val !== 'number';

export const ClientPalletList = (props) => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    // The table reports the rows it holds (every page, filtered and sorted as
    // on screen); the Export writes exactly those. The count is state so the
    // button can disable itself when a filter leaves nothing to export.
    const rowsRef = useRef([]);
    const [rowCount, setRowCount] = useState(0);
    const handleRowsChange = useCallback((rows) => {
        rowsRef.current = rows;
        setRowCount(rows.length);
    }, []);
    const getRows = useCallback(() => rowsRef.current, []);

    return (
        <List title={ListTitle}
              sort={listSort}
              perPage={12}
              filters={<ListFilter/>}
              filterDefaultValues={{active: true}}
              actions={isSmall ? <ListActions/> : <ListActions getRows={getRows} rowCount={rowCount}/>}
              bulkActionButtons={false}
              pagination={false}
              {...props}
        >
            {isSmall ? (
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => `Pallets: ${(record.pallet_qty) ? record.pallet_qty : 0}`}
                    tertiaryText={record => record.id}
                />
            ) : (
                <DataTable columns={listColumns} sort={listSort} onRowsChange={handleRowsChange}
                           fixedLayout={true} messageEmpty=" No Clients found." {...props}/>
            )}
        </List>
    )
};

/**
 * View: Show
 */

const ShowTitle = ({record}) => {
    return <span><Avatar style={viewStyles.avatar} className="avatar-title"
                         alt="Pallet Storage"><ClientPalletIcon
        style={viewStyles.avatarSvg}/></Avatar>{record ? `${record.name}` : 'Pallet Storage'}</span>;
};

export const ClientPalletShow = (props) => (
    <Show title={<ShowTitle/>} {...props}>
        <SimpleShowLayout className="show-layout">
            <FieldDescription
                instructions={true} marginBottom={true}
                text={`
                Review this Client's pallet count and its adjustment history below.
                `}
            />
            <TextField label="ID" source="id" className="input-inline input-120"/>
            <TextField label="Name of Client" source="name" className="input-inline input-280"/>
            <NumberField label="Pallets" source="pallet_qty" className="input-inline input-90"/>
            <PalletQtyEdit className="input-inline input-edit-pallet"/>
            <PalletLedgerTable record={props.record} {...props}/>
        </SimpleShowLayout>
    </Show>
);
